import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBriefcase, FiArrowLeft, FiMapPin, FiClock, FiDollarSign, FiTrendingUp, FiUsers, FiAward, FiCheckCircle, FiMail, FiPhone, FiChevronDown, FiChevronUp, FiUserPlus, FiShare2, FiCheck } = FiIcons;

const CareersPage = () => {
  const navigate = useNavigate();
  const [expandedJob, setExpandedJob] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);

  const contactInfo = {
    name: "Thomas Fugleberg",
    email: "thomas@hmsvarsling.no",
    phone: "40 55 19 89"
  };

  const handleApply = (jobTitle) => {
    window.location.href = `mailto:${contactInfo.email}?subject=Søknad: ${jobTitle}`;
  };

  const handleShare = (e, jobId) => {
    e.stopPropagation();
    const url = `${window.location.origin}/#/karriere?job=${jobId}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(jobId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const jobs = [
    {
      id: 'b2b-selger',
      title: 'B2B Selger',
      type: 'Heltid / Provisjon',
      location: 'Oslo / Remote',
      department: 'Salg',
      icon: FiTrendingUp,
      shortDesc: 'Er du en sulten selger som motiveres av gode resultater og høy inntjening? Vi søker deg som vil selge et lovpålagt produkt alle bedrifter trenger.',
      fullDesc: (
        <div className="space-y-4 text-gray-600">
          <p>
            HMS Varsling er i kraftig vekst, og vi skal nå styrke vårt salgsteam mot bedriftsmarkedet. Vi selger en digital plattform som hjelper bedrifter å oppfylle kravene i Arbeidsmiljøloven og EUs varslingsdirektiv. Dette er et produkt med høy aktualitet og stor verdi for kundene.
          </p>
          <h4 className="font-bold text-gray-900 mt-4">Dine arbeidsoppgaver:</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>Oppsøkende salg mot små og mellomstore bedrifter (SMB).</li>
            <li>Booke og gjennomføre digitale salgsmøter/demoer.</li>
            <li>Bygge og forvalte din egen portefølje.</li>
            <li>Forhandle og lukke avtaler.</li>
          </ul>
          <h4 className="font-bold text-gray-900 mt-4">Vi ser etter deg som:</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>Har erfaring med B2B-salg (eller et sterkt ønske om å lære).</li>
            <li>Er uredd, strukturert og selvgående.</li>
            <li>Kommuniserer godt på norsk, både muntlig og skriftlig.</li>
            <li>Motiveres av budsjetter og resultater.</li>
          </ul>
          <h4 className="font-bold text-gray-900 mt-4">Vi tilbyr:</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>Meget gode provisjonsmodeller uten tak.</li>
            <li>Et ferdigutviklet, lovpålagt produkt som er "lett" å selge.</li>
            <li>Full opplæring og tett oppfølging.</li>
            <li>Mulighet for rask karriereutvikling internt.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'partner',
      title: 'Partner (Selvstendig)',
      type: 'Frilans / Selvstendig',
      location: 'Hele Norge',
      department: 'Partnerprogram',
      icon: FiUserPlus,
      isPartner: true, // Special flag for formatting
      shortDesc: 'Driver du egen virksomhet? Ønsker du flere ben å stå på? Bli forhandler av HMS Varsling og bygg din egen portefølje med tilbakevendende inntekter.',
      fullDesc: (
        <div className="space-y-4 text-gray-600">
          <p>
            Som Partner i HMS Varsling opererer du som selvstendig næringsdrivende. Du får tilgang til vår partnerportal, salgsmateriell og full støtte, men du styrer din egen hverdag. Dette passer perfekt for konsulenter, regnskapsførere, eller erfarne selgere som vil bygge noe eget.
          </p>
          <h4 className="font-bold text-gray-900 mt-4">Hvorfor bli partner?</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Tilbakevendende inntekt (Recurring Revenue):</strong> Du får provisjon så lenge kunden er aktiv.</li>
            <li>Ingen kostnad for å starte.</li>
            <li>Du får din egen unike verve-link og full oversikt i vårt dashboard.</li>
            <li>Vi tar oss av support, fakturering og teknisk drift – du fokuserer kun på relasjonen.</li>
          </ul>
          <h4 className="font-bold text-gray-900 mt-4">Hvem passer dette for?</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>Du som har et eksisterende nettverk i B2B-markedet.</li>
            <li>Du som driver med HMS-rådgivning, HR, eller regnskap.</li>
            <li>Du som ønsker friheten til å jobbe hvor og når du vil.</li>
          </ul>
          <div className="bg-blue-50 p-4 rounded-lg mt-4 border border-blue-100">
            <p className="text-blue-800 font-medium">
              Du trenger ikke sende søknad. Du kan registrere deg direkte som partner i dag og komme i gang umiddelbart.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'teamleder',
      title: 'Teamleder Salg',
      type: 'Fast stilling',
      location: 'Oslo',
      department: 'Salgsledelse',
      icon: FiUsers,
      shortDesc: 'Har du ledererfaring og evnen til å få andre til å prestere? Vi søker en operativ Teamleder som skal bygge og lede et team av sultne selgere.',
      fullDesc: (
        <div className="space-y-4 text-gray-600">
          <p>
            Som Teamleder Salg vil du ha en nøkkelrolle i selskapets vekst. Du vil få ansvar for å rekruttere, lære opp og lede ditt eget salgsteam. Du må trives med å være operativ – det vil si at du skal selge selv, samtidig som du coacher andre.
          </p>
          <h4 className="font-bold text-gray-900 mt-4">Ansvarsområder:</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>Daglig oppfølging og coaching av selgere.</li>
            <li>Ansvar for teamets totalbudsjett.</li>
            <li>Gjennomføre medlytt og sambesøk (digitale møter).</li>
            <li>Rekruttering og onboarding av nye talenter.</li>
            <li>Rapportering til Salgssjef/Daglig leder.</li>
          </ul>
          <h4 className="font-bold text-gray-900 mt-4">Kvalifikasjoner:</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>Dokumenterte salgsresultater (B2B).</li>
            <li>Erfaring med personalledelse eller teamledelse er en fordel.</li>
            <li>Strukturert, tydelig og motiverende lederstil.</li>
            <li>Evne til å skape entusiasme og vinnerkultur.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'salgssjef',
      title: 'Salgssjef',
      type: 'Fast stilling / Management',
      location: 'Oslo',
      department: 'Ledelse',
      icon: FiBriefcase,
      shortDesc: 'Strategisk og operativ stilling for deg som vil forme fremtidens salgsavdeling. Du får totalansvar for salgsstrategi, budsjetter og partnersatsning.',
      fullDesc: (
        <div className="space-y-4 text-gray-600">
          <p>
            HMS Varsling skal ta en ledende posisjon i markedet, og vi trenger en visjonær og handlekraftig Salgssjef. Du vil jobbe tett med daglig leder og styret for å legge strategien for nasjonal vekst.
          </p>
          <h4 className="font-bold text-gray-900 mt-4">Ditt oppdrag:</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>Utvikle og implementere selskapets salgsstrategi.</li>
            <li>Totalansvar for budsjett, prognoser og resultater.</li>
            <li>Ledelse av teamledere og partneransvarlige.</li>
            <li>Videreutvikle salgsprosesser, CRM-bruk og KPI-styring.</li>
            <li>Identifisere nye markedssegmenter og inntektsstrømmer.</li>
          </ul>
          <h4 className="font-bold text-gray-900 mt-4">Vi tilbyr:</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>En sentral rolle i et selskap med store ambisjoner.</li>
            <li>Konkurransedyktige betingelser og opsjonsmuligheter.</li>
            <li>Stor påvirkningskraft og korte beslutningsveier.</li>
            <li>Et profesjonelt og dynamisk miljø.</li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
              <SafeIcon icon={FiArrowLeft} /> <span>Tilbake til forsiden</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Karriere i HMS Varsling</h1>
            <div className="w-20"></div> {/* Spacer */}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Bli med på reisen</h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Vi bygger Norges tryggeste plattform for varsling og HMS. Vi vokser raskt og trenger dyktige folk som vil være med å gjøre en forskjell i arbeidslivet.
          </p>
        </div>
      </div>

      {/* Job Listings */}
      <div className="max-w-5xl mx-auto px-4 py-16 -mt-10">
        <div className="space-y-6">
          {jobs.map((job) => (
            <motion.div 
              key={job.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
            >
              <div 
                className="p-6 md:p-8 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className="p-4 bg-blue-100 text-blue-600 rounded-xl">
                      <SafeIcon icon={job.icon} className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-2">
                        <span className="flex items-center gap-1">
                          <SafeIcon icon={FiBriefcase} /> {job.type}
                        </span>
                        <span className="flex items-center gap-1">
                          <SafeIcon icon={FiMapPin} /> {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <SafeIcon icon={FiUsers} /> {job.department}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* PC View Button */}
                    <span className="hidden md:flex items-center gap-2 text-blue-600 font-medium">
                      Les mer <SafeIcon icon={expandedJob === job.id ? FiChevronUp : FiChevronDown} />
                    </span>
                  </div>
                </div>
                {/* Mobile View Arrow */}
                <div className="md:hidden flex justify-center mt-4 text-blue-600">
                  <SafeIcon icon={expandedJob === job.id ? FiChevronUp : FiChevronDown} className="text-2xl" />
                </div>
              </div>

              <AnimatePresence>
                {expandedJob === job.id && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-gray-100 bg-gray-50"
                  >
                    <div className="p-6 md:p-8">
                      <p className="text-lg font-medium text-gray-800 mb-6 italic">
                        "{job.shortDesc}"
                      </p>
                      
                      <div className="prose prose-blue max-w-none mb-8">
                        {job.fullDesc}
                      </div>

                      <div className="bg-white p-6 rounded-lg border border-gray-200 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                            {/* Placeholder for Thomas image or generic user icon */}
                            <SafeIcon icon={FiUsers} className="text-gray-500 text-xl" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Kontaktperson</p>
                            <p className="font-bold text-gray-900">{contactInfo.name}</p>
                            <div className="flex gap-4 text-sm text-gray-600 mt-1">
                              <a href={`mailto:${contactInfo.email}`} className="hover:text-blue-600 flex items-center gap-1">
                                <SafeIcon icon={FiMail} /> {contactInfo.email}
                              </a>
                              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="hover:text-blue-600 flex items-center gap-1">
                                <SafeIcon icon={FiPhone} /> {contactInfo.phone}
                              </a>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-3 w-full md:w-auto">
                            {/* NY DELINGSKNAPP */}
                            <button 
                                onClick={(e) => handleShare(e, job.id)} 
                                className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-600 flex items-center justify-center gap-2"
                                title="Kopier lenke"
                            >
                                {copiedLink === job.id ? <SafeIcon icon={FiCheck} className="text-green-600" /> : <SafeIcon icon={FiShare2} />}
                                {copiedLink === job.id ? 'Kopiert!' : 'Del'}
                            </button>

                            {job.isPartner ? (
                            <button 
                                onClick={() => navigate('/partner/register')}
                                className="flex-1 md:flex-none bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors font-bold shadow-md flex items-center justify-center gap-2"
                            >
                                <SafeIcon icon={FiUserPlus} /> Registrer deg som Partner
                            </button>
                            ) : (
                            <button 
                                onClick={() => handleApply(job.title)}
                                className="flex-1 md:flex-none bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-bold shadow-md flex items-center justify-center gap-2"
                            >
                                <SafeIcon icon={FiMail} /> Send søknad på e-post
                            </button>
                            )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* General Application */}
        <div className="mt-16 text-center bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Fant du ikke stillingen du lette etter?</h3>
          <p className="text-gray-600 mb-6">
            Vi er alltid på jakt etter dyktige folk. Send oss gjerne en åpen søknad.
          </p>
          <button onClick={() => handleApply('Åpen søknad')} className="text-blue-600 font-medium hover:underline flex items-center justify-center gap-2 mx-auto">
            <SafeIcon icon={FiMail} /> Send åpen søknad til Thomas
          </button>
        </div>
      </div>
    </div>
  );
};

export default CareersPage;